import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import VideoSection from '../components/VideoSection';
import JournalPreviewSection from '../components/JournalPreviewSection';
import MyStoryPreview from '../components/MyStoryPreview';
import ShopBanner from '../components/ShopBanner';
import ProductsCarousel from '../components/ProductsCarousel';
import SocialMediaLinks from '../components/SocialMediaLinks';
import { useLanguage } from '../context/LanguageContext';
import { 
  ArrowRight, 
  ExternalLink,
  Star,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Affiliate LifeWave Shop Link
const LIFEWAVE_SHOP_URL = 'https://lifewave.com/ioanaalexandra/store/products';

const HomePage = () => {
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translatedDescription, setTranslatedDescription] = useState('');
  const [translatedQuote, setTranslatedQuote] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const { t, language, translateContent } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch site settings from public endpoint
        const settingsRes = await axios.get(`${API_URL}/api/settings`);
        if (settingsRes.data) {
          setSiteSettings(settingsRes.data);
        }
        
        // Seed data
        await axios.post(`${API_URL}/api/seed`);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Translate hero content when language changes
  useEffect(() => {
    const translateHeroContent = async () => {
      if (!siteSettings) return;
      
      const description = siteSettings.hero_description || '';
      const quote = siteSettings.hero_quote || '';
      
      // If Romanian, use original text
      if (language === 'ro') {
        setTranslatedDescription(description);
        setTranslatedQuote(quote);
        return;
      }

      // Translate to selected language
      setIsTranslating(true);
      try {
        const [translatedDesc, translatedQ] = await Promise.all([
          description ? translateContent(description, 'hero_description', 'hero_desc') : '',
          quote ? translateContent(quote, 'hero_quote', 'hero_quote') : ''
        ]);
        setTranslatedDescription(translatedDesc);
        setTranslatedQuote(translatedQ);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedDescription(description);
        setTranslatedQuote(quote);
      } finally {
        setIsTranslating(false);
      }
    };

    translateHeroContent();
  }, [siteSettings, language, translateContent]);

  // Use settings if available, otherwise fall back to translations
  const heroTitle = siteSettings?.hero_title || t('heroTitle');
  const heroDescription = translatedDescription || siteSettings?.hero_description || t('heroDescription');
  const heroImage = siteSettings?.hero_image || "https://images.unsplash.com/photo-1746554740031-901926d8b025?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHx3ZWxsbmVzcyUyMGhlYWx0aHklMjBsaWZlc3R5bGUlMjB3b21hbiUyMGhhcHB5JTIwZW5lcmd5JTIwdml0YWxpdHl8ZW58MHx8fHwxNzcyNDc2NTYyfDA&ixlib=rb-4.1.0&q=85&w=600";
  const heroQuote = translatedQuote || siteSettings?.hero_quote;

  // Show loading skeleton for hero while settings load
  if (loading) {
    return (
      <div className="min-h-screen" data-testid="home-page">
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-100 py-24 lg:py-32 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 animate-pulse">
                <div className="h-8 w-40 bg-slate-200 rounded-full"></div>
                <div className="h-16 w-3/4 bg-slate-200 rounded-xl"></div>
                <div className="h-24 w-full bg-slate-200 rounded-xl"></div>
                <div className="flex gap-4">
                  <div className="h-12 w-40 bg-slate-200 rounded-full"></div>
                  <div className="h-12 w-40 bg-slate-200 rounded-full"></div>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="aspect-square w-full max-w-md bg-slate-200 rounded-3xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-100 py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-sky-200/30 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-200/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {/* Stylish badge */}
              <div 
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                  color: 'white',
                  fontFamily: "'Poppins', sans-serif",
                  boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)'
                }}
              >
                <Star className="w-4 h-4 fill-current" />
                {t('officialPartner')}
              </div>
              
              {/* Stylish gradient title with 3D effect */}
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight"
                style={{
                  background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 50%, #38bdf8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '3px 3px 6px rgba(3, 105, 161, 0.2)',
                  fontFamily: "'Poppins', 'Segoe UI', sans-serif"
                }}
              >
                {heroTitle}
              </h1>
              
              {/* Stylish description with gradient */}
              {isTranslating ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{language === 'de' ? 'Übersetzen...' : 'Translating...'}</span>
                </div>
              ) : (
                <p 
                  className="text-xl sm:text-2xl max-w-lg leading-relaxed whitespace-pre-line font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, #334155 0%, #64748b 50%, #94a3b8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontFamily: "'Poppins', 'Segoe UI', sans-serif",
                    textShadow: '1px 1px 2px rgba(51, 65, 85, 0.1)'
                  }}
                >
                  {heroDescription}
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="text-white rounded-full px-8 font-bold text-lg shadow-xl hover:scale-105 transition-all duration-300"
                  style={{
                    background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
                    boxShadow: '0 8px 24px rgba(3, 105, 161, 0.4)',
                    fontFamily: "'Poppins', sans-serif"
                  }}
                  onClick={() => window.open(LIFEWAVE_SHOP_URL, '_blank')}
                  data-testid="hero-shop-btn"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  {t('shopOnLifeWave')}
                </Button>
                <Link to="/products">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full px-8 font-bold text-lg border-2 border-sky-500 text-sky-600 hover:bg-sky-50 hover:scale-105 transition-all duration-300"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                    data-testid="hero-browse-btn"
                  >
                    {t('browseProducts')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img 
                  src={heroImage}
                  alt="Happy woman enjoying wellness lifestyle"
                  className="rounded-3xl shadow-2xl shadow-slate-200/50 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-amber-400 to-amber-500 rounded-3xl -z-0 opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section - appears only if quote exists */}
      {heroQuote && heroQuote.trim() && (
        <section className="py-12 bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            {/* Quote marks */}
            <div className="text-6xl text-white/20 font-serif leading-none mb-4">"</div>
            
            {/* Quote text with beautiful gradient and 3D effect */}
            <blockquote 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-relaxed"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #e0f2fe 50%, #bae6fd 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                fontFamily: "'Poppins', 'Georgia', serif",
                fontStyle: 'italic'
              }}
            >
              {heroQuote}
            </blockquote>
            
            {/* Closing quote */}
            <div className="text-6xl text-white/20 font-serif leading-none mt-4 rotate-180">"</div>
          </div>
        </section>
      )}

      {/* My Story Preview Section */}
      <MyStoryPreview />

      {/* Journal Preview Section */}
      <JournalPreviewSection />

      {/* Shop Banner */}
      <ShopBanner />

      {/* Video Section */}
      <VideoSection />

      {/* Products Carousel - at the bottom */}
      <ProductsCarousel />

      {/* Footer CTA with Social Links */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            {t('readyExperience')}
          </h2>
          <p className="text-slate-400 mb-6 max-w-xl mx-auto">
            {t('ctaDescription')}
          </p>
          
          {/* Social Media Links */}
          <div className="flex justify-center">
            <SocialMediaLinks />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
